import { createReducer } from 'redux-create-reducer'
import type { RecipientList, RecipientListId, MailingList } from 'types/list'
import {
  BUSINESS_SETTINGS_UPDATE,
  BUSINESS_SETTINGS_UPDATE_FAIL,
  BUSINESS_SETTINGS_UPDATE_SUCCESS,
  CONTACTS_ADD_LIST,
  CONTACTS_ADD_LIST_FAIL,
  CONTACTS_ADD_LIST_SUCCESS,
  CONTACTS_CREATE_SUCCESS,
  CONTACTS_IMPORT_SUCCESS,
  CONTACTS_REMOVE_LIST,
  CONTACTS_REMOVE_LIST_FAIL,
  EMAIL_GROUP_CREATE,
  EMAIL_GROUP_CREATE_FAIL,
  EMAIL_GROUP_CREATE_SUCCESS,
  EMAIL_GROUP_DELETE,
  EMAIL_GROUP_DELETE_FAIL,
  EMAIL_GROUP_DELETE_SUCCESS,
  EMAIL_GROUP_GET,
  EMAIL_GROUP_GET_FAIL,
  EMAIL_GROUP_GET_SUCCESS,
  EMAIL_GROUP_UPDATE,
  EMAIL_GROUP_UPDATE_FAIL,
  EMAIL_GROUP_UPDATE_SUCCESS,
  EMAIL_GROUPS_LOAD,
  EMAIL_GROUPS_LOAD_FAIL,
  EMAIL_GROUPS_LOAD_SUCCESS,
  CONTACT_EMAIL_GROUPS_LOAD,
  CONTACT_EMAIL_GROUPS_LOAD_FAIL,
  CONTACT_EMAIL_GROUPS_LOAD_SUCCESS,
  MAILING_LIST_LOAD,
  MAILING_LIST_LOAD_FAIL,
  MAILING_LIST_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { contactToRecipient } from '@disco/utils/contacts'
export type EmailGroupsState = {
  isFetching: boolean
  isFetched: boolean
  isSaving: boolean
  isDeleting: boolean
  isFetchingMailingLists: boolean
  mailingListsFetched: boolean
  error: Error | null | undefined
  contactEmailGroups: Record<string, any>[]
  emailGroups: Array<RecipientList>
  mailingList: Array<MailingList>
}
const initialState: EmailGroupsState = {
  isFetching: false,
  isFetched: false,
  isSaving: false,
  isDeleting: false,
  isFetchingMailingLists: false,
  mailingListsFetched: false,
  error: null,
  emailGroups: [],
  contactEmailGroups: [],
  mailingList: [],
}

function updateGroup<T extends { id: number }>(
  items: Array<T>,
  id: RecipientListId,
  updater: (arg0: T) => T,
): Array<T> {
  const updatedItemIndex = items.findIndex(item => item.id === id)
  return [
    ...items.slice(0, updatedItemIndex),
    // updatedItemIndex was found by findIndex, so items[updatedItemIndex] is defined
    updater(items[updatedItemIndex]!),
    ...items.slice(updatedItemIndex + 1),
  ]
}

export const emailGroupsReducer = createReducer<EmailGroupsState, any>(
  initialState,
  {
    [EMAIL_GROUPS_LOAD](state) {
      return {
        ...state,
        isFetching: true,
        isFetched: false,
      }
    },

    [EMAIL_GROUPS_LOAD_SUCCESS](state, action) {
      return {
        ...state,
        isFetching: false,
        isFetched: true,
        error: null,
        emailGroups: action.emailGroups,
      }
    },

    [EMAIL_GROUPS_LOAD_FAIL](state, action) {
      return {
        ...state,
        isFetching: false,
        isFetched: false,
        error: action.error,
      }
    },

    [MAILING_LIST_LOAD](state) {
      return {
        ...state,
        isFetchingMailingLists: true,
        mailingListsFetched: false,
        mailingList: [],
      }
    },

    [MAILING_LIST_LOAD_SUCCESS](state, action) {
      return {
        ...state,
        isFetchingMailingLists: false,
        mailingListsFetched: true,
        error: null,
        mailingList: action.mailingList,
      }
    },

    [MAILING_LIST_LOAD_FAIL](state, action) {
      return {
        ...state,
        isFetchingMailingLists: false,
        mailingListsFetched: false,
        error: action.error,
      }
    },

    [CONTACT_EMAIL_GROUPS_LOAD](state) {
      return {
        ...state,
        isFetching: true,
        isFetched: false,
        contactEmailGroups: [],
      }
    },

    [CONTACT_EMAIL_GROUPS_LOAD_SUCCESS](state, action) {
      return {
        ...state,
        isFetching: false,
        isFetched: true,
        error: null,
        contactEmailGroups: action.contactEmailGroups,
      }
    },

    [CONTACT_EMAIL_GROUPS_LOAD_FAIL](state, action) {
      return {
        ...state,
        isFetching: false,
        isFetched: false,
        error: action.error,
      }
    },

    [EMAIL_GROUP_GET](state) {
      return { ...state, isFetching: true, isFetched: false }
    },

    [EMAIL_GROUP_GET_SUCCESS](state, action) {
      const { emailGroups } = state
      let getItemIndex = -1

      for (let i = 0; i < emailGroups.length; i++) {
        // i is within bounds since loop limit is emailGroups.length
        if (emailGroups[i]!.id === action.emailGroup.id) {
          getItemIndex = i
          break
        }
      }

      if (getItemIndex > -1) {
        return {
          ...state,
          isFetching: false,
          isFetched: true,
          error: null,
          emailGroups: [
            ...emailGroups.slice(0, getItemIndex),
            action.emailGroup,
            ...emailGroups.slice(getItemIndex + 1),
          ],
        }
      } else {
        return {
          ...state,
          isFetching: false,
          isFetched: true,
          error: null,
          emailGroups: [...emailGroups, action.emailGroup],
        }
      }
    },

    [EMAIL_GROUP_GET_FAIL](state, action) {
      return {
        ...state,
        isFetching: false,
        isFetched: false,
        error: action.error,
      }
    },

    [EMAIL_GROUP_DELETE](state) {
      return { ...state, isDeleting: true }
    },

    [EMAIL_GROUP_DELETE_SUCCESS](state, action) {
      const { emailGroups, mailingList } = state

      return {
        ...state,
        isDeleting: false,
        error: null,
        emailGroups: emailGroups.filter(g => g.id !== action.emailGroup.id),
        mailingList: mailingList.filter(g => g.id !== action.emailGroup.id),
      }
    },

    [EMAIL_GROUP_DELETE_FAIL](state, action) {
      return { ...state, isDeleting: false, error: action.error }
    },

    [BUSINESS_SETTINGS_UPDATE](state) {
      return { ...state, isSaving: true, error: null }
    },

    [BUSINESS_SETTINGS_UPDATE_SUCCESS](state) {
      return { ...state, isSaving: false }
    },

    [BUSINESS_SETTINGS_UPDATE_FAIL](state) {
      return { ...state, isSaving: false }
    },

    [EMAIL_GROUP_UPDATE](state) {
      return { ...state, isSaving: true, error: null }
    },

    [EMAIL_GROUP_UPDATE_SUCCESS](state, action) {
      return {
        ...state,
        isSaving: false,
        error: null,
        emailGroups: updateGroup(
          // bug,
          state.emailGroups,
          action.emailGroup.id,
          () => action.emailGroup,
        ),
      }
    },

    [EMAIL_GROUP_UPDATE_FAIL](state, action) {
      return { ...state, isSaving: false, error: action.error }
    },

    [EMAIL_GROUP_CREATE](state) {
      return { ...state, isSaving: true }
    },

    [EMAIL_GROUP_CREATE_SUCCESS](state, action) {
      const { emailGroups, mailingList } = state
      return {
        ...state,
        isSaving: false,
        error: null,
        emailGroups: [...emailGroups, action.emailGroup],
        mailingList: [
          ...mailingList,
          {
            ...action.emailGroup,
            count_recipients: 0,
          },
        ],
      }
    },

    [EMAIL_GROUP_CREATE_FAIL](state, action) {
      return { ...state, isSaving: false, error: action.error }
    },

    [CONTACTS_CREATE_SUCCESS](state, action) {
      if (!action.listId) {
        return state
      }

      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const adding = [action.contact].map(c => contactToRecipient(c))
          return { ...group, recipients: group.recipients.concat(adding) }
        },
      )

      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_IMPORT_SUCCESS](state, action) {
      if (!action.listId) {
        return state
      }

      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const adding = action.contacts.map(c => contactToRecipient(c))
          return { ...group, recipients: group.recipients.concat(adding) }
        },
      )

      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_ADD_LIST](state, action) {
      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const existing = group.recipients
            .map(rec => (rec.contact ? rec.contact.id : null))
            .filter(id => id)
          const adding = action.contacts
            .filter(rec => !existing.includes(rec.id))
            .map(contact => {
              const user = contactToRecipient(contact)
              return user
            })
          let notLoadedContacts = []

          if (action.ids) {
            notLoadedContacts = action.ids
              .filter(
                id =>
                  !existing.includes(id) &&
                  !action.contacts.find(contact => contact.id === id),
              )
              .map(id => {
                return {
                  contact: {
                    id: id,
                  },
                }
              })
          }

          return {
            ...group,
            recipients: group.recipients.concat(adding, notLoadedContacts),
          }
        },
      )

      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_ADD_LIST_SUCCESS](state, action) {
      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          return {
            ...group,
            recipients: action.failed
              ? group.recipients.filter(rec =>
                  rec.contact ? !action.failed.includes(rec.contact.id) : true,
                )
              : group.recipients,
          }
        },
      )
      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_ADD_LIST_FAIL](state, action) {
      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const failed = action.ids || action.contacts.map(rec => rec.id)
          return {
            ...group,
            recipients: group.recipients.filter(rec =>
              rec.contact ? !failed.includes(rec.contact.id) : true,
            ),
          }
        },
      )
      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_REMOVE_LIST](state, action) {
      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const removing = action.contacts.map(rec => rec.id)
          return {
            ...group,
            recipients: group.recipients.filter(rec =>
              rec.contact ? !removing.includes(rec.contact.id) : true,
            ),
          }
        },
      )
      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },

    [CONTACTS_REMOVE_LIST_FAIL](state, action) {
      const updatedGroups = updateGroup(
        state.emailGroups,
        action.listId,
        group => {
          const existing = group.recipients.map(rec => rec.id)
          const failed = action.contacts
            .filter(rec => !existing.includes(rec.id))
            .map(contact => {
              const user = {
                contact,
              }
              return user
            })
          return { ...group, recipients: group.recipients.concat(failed) }
        },
      )
      return {
        ...state,
        emailGroups: updatedGroups,
        mailingList: updateMailingList(
          state.mailingList,
          action.listId,
          updatedGroups,
        ),
      }
    },
  },
)

const updateMailingList = (
  mailingLists: Array<MailingList>,
  id: RecipientListId,
  updatedGroups: Array<RecipientList>,
): Array<MailingList> => {
  return updateGroup(mailingLists, id, mailingList => {
    const group = updatedGroups.find(g => g.id === id)
    return {
      ...mailingList,
      count_recipients: group?.recipients.length ?? 0,
    }
  })
}
